import moment from 'moment';
import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useGlobalize} from 'react-native-globalize';
import FontAwesome5Pro from 'react-native-vector-icons/FontAwesome5Pro';
import Localized from 'src/constants/AppStrings';
import Styles from '../../Styles';
import AVFormattedCurrency from '../AVFormattedCurrency';
import type {HistoryItem as HistoryItemType} from 'src/types/TransactionDetail';
import AVTouchableOpacity from '../AVTouchableOpacity';
import AccountStore from 'src/stores/AccountStore';
import AccountConstants from 'src/constants/AccountConstants';
import AVText from '../AVText';
import {BalanceTypes} from 'src/types/serverTypes/Account';

const REFUND_TYPE = 'refund';

type HistoryItemProps = {
  onPress: (item: HistoryItemType) => void;
  historyItem: HistoryItemType;
  index: number;
};

export const Reason = {
  Referral: 'Referral',
  SignUp: 'Referral-Signup',
};

const HistoryItem = (props: HistoryItemProps) => {
  const {formatCurrency} = useGlobalize();
  let {Type, TransDate, Source, balanceType, Amount} = props.historyItem;

  if (Type === AccountConstants.SALE_TYPE) {
    Amount *= -1;
  }

  const isClickable =
    Type === AccountConstants.REFERRAL_TYPE ||
    Type === AccountConstants.REFERRAL_SIGNUP_TYPE ||
    Type === AccountConstants.SALE_TYPE ||
    Type === AccountConstants.FUNDING_TYPE ||
    Type === AccountConstants.REFUND_TYPE ||
    Type === AccountConstants.REDEMPTION_TYPE;

  const type = getTranslatedType(Type, Source);
  const balanceTypeLabel = getTranslatedBalanceType(balanceType);
  const date = moment(TransDate).calendar(null, {
    sameDay: `[${Localized.Labels.today}]`,
    lastDay: `[${Localized.Labels.yesterday}]`,
    lastWeek: 'dddd',
    sameElse: 'MMM D',
  });

  const formattedCurrency = formatCurrency(Amount, AccountStore.getCurrency(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <AVTouchableOpacity
      accessible={false}
      accessibilityLabel={`History Item ${Type} ${props.index}`}
      style={styles.row}
      disabled={!isClickable}
      onPress={() => props.onPress(props.historyItem)}
    >
      <View
        accessible={true}
        accessibilityElementsHidden={false}
        accessibilityLabel={`${type} ${date} for ${formattedCurrency}`}
        nativeID={`${type} ${date} for ${formattedCurrency}`}
        style={styles.messageContainer}
      >
        <View style={styles.amountContainer}>
          <View style={Styles.Style.row}>
            <AVText numberOfLines={1} style={styles.item}>
              {type}
            </AVText>
          </View>
          <View style={Styles.Style.row}>
            <AVText style={styles.date}>{balanceTypeLabel}</AVText>
          </View>
        </View>
        <View style={styles.amountView}>
          <AVText style={styles.date}> </AVText>
          <AVFormattedCurrency
            value={Amount}
            style={styles.amount}
            accessibilityLabel="amount"
          />
          <AVText style={styles.date}>{date}</AVText>
        </View>
        <View style={styles.arrow}>
          {isClickable && (
            <FontAwesome5Pro
              name="chevron-right"
              color="#999"
              size={Styles.Fonts.f2}
              light
            />
          )}
        </View>
      </View>
    </AVTouchableOpacity>
  );
};

const getTranslatedBalanceType = (balanceType) => {
  switch (balanceType) {
    case BalanceTypes.Default:
    case BalanceTypes.Stripe:
    case BalanceTypes.Split:
      return Localized.Labels.my_account;
    case BalanceTypes.RollOver:
    case BalanceTypes.TopOff:
      return Localized.Labels.subsidy;
    case BalanceTypes.Payroll:
      return Localized.Labels.payroll_deduct;
  }
};

const getTranslatedType = (Type, Source) => {
  switch (Type) {
    case AccountConstants.REFERRAL_TYPE: {
      return Localized.Labels.referral_reward;
    }
    case AccountConstants.REFERRAL_SIGNUP_TYPE: {
      return Localized.Labels.sign_up_reward;
    }
    case AccountConstants.SEND_SNACK:
    case AccountConstants.SEND_SNACK_ACCEPT:
    case AccountConstants.SEND_SNACK_CANCEL:
    case AccountConstants.SEND_SNACK_EXPIRE:
    case AccountConstants.SEND_SNACK_REVERSE: {
      return Localized.Labels.snack_credit;
    }
    case AccountConstants.SALE_TYPE: {
      return Localized.Labels.purchase;
    }
    case AccountConstants.FUNDING_TYPE:
    case 'addvalue': {
      return Localized.Labels.funding;
    }
    case 'adjustment': {
      return Localized.Labels.adjustment;
    }
    case AccountConstants.REDEMPTION_TYPE: {
      return Localized.Labels.loyalty_credit;
    }
    case REFUND_TYPE: {
      return Localized.Labels.refund;
    }
    case AccountConstants.TRANSFER_IN:
    case AccountConstants.TRANSFER_OUT: {
      return Localized.Labels.balance_transfer;
    }
    default: {
      if (Source === AccountConstants.MOBLICO_SOURCE) {
        return Localized.Labels.promotion;
      }

      return Type;
    }
  }
};

const styles = StyleSheet.create({
  amount: {
    alignSelf: 'flex-end',
    color: Styles.black,
    fontSize: Styles.Fonts.f1,
    fontWeight: '300',
    textAlign: 'right',
    paddingRight: Styles.Spacing.m1,
  },
  amountContainer: {
    flex: 1,
    paddingRight: Styles.Spacing.m3,
  },
  amountView: {
    alignItems: 'center',

    justifyContent: 'center',
  },
  arrow: {
    alignItems: 'center',
    justifyContent: 'center',
    width: Styles.Fonts.f1,
  },
  balance: {
    color: Styles.gray40,
    flex: 1.5,
    fontSize: Styles.Fonts.f5,
    fontWeight: '300',
    textAlign: 'right',
  },
  date: {
    alignSelf: 'flex-start',
    color: Styles.black,
    flex: 0.5,
    fontSize: Styles.Fonts.f0,
    fontWeight: '300',
  },
  id: {
    alignSelf: 'flex-start',
    color: Styles.davyGray,
    flex: 0.75,
    fontSize: Styles.Fonts.f0,
    fontWeight: '300',
  },
  item: {
    alignSelf: 'flex-start',
    color: Styles.black,
    flex: 2,
    fontSize: Styles.Fonts.f2,
    fontWeight: '300',
    textTransform: 'capitalize',
  },
  messageContainer: {
    borderBottomColor: Styles.gray,
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    width: '100%',
    padding: Styles.Spacing.m3,
  },
  row: {
    alignItems: 'center',
    backgroundColor: Styles.white,
    flexDirection: 'column',
    justifyContent: 'center',
  },
});

export default HistoryItem;
